/**
 * A custom hook for local storage.
 *
 * @param {string} key - The key to store the value in local storage.
 * @param {any} defaultValue - The default value to return if the key is not found in local storage.
 * @returns {[any, Function]} - The value and a function to set the value.
 */
import { useEffect, useMemo, useState } from 'react';

/**
 * Namespaces a localStorage key with the app name (e.g. `nucleus|freeze-grid`).
 *
 * Exported so that anything reading or clearing persisted UI state uses the
 * exact same key this hook writes — mismatched prefixes silently orphan the
 * stored value.
 */
export const namespacedKey = (key: string) =>
  `${(import.meta.env.VITE_APP_NAME ?? 'nucleus').toLowerCase()}|${key}`;

const useLocalStorage = (key: string, defaultValue: unknown) => {
  const formattedKey = useMemo(() => namespacedKey(key), [key]);

  const [value, setValue] = useState(() => {
    const storedValue = localStorage.getItem(formattedKey);
    return storedValue ? JSON.parse(storedValue) : defaultValue;
  });

  useEffect(() => {
    localStorage.setItem(formattedKey, JSON.stringify(value));
  }, [formattedKey, value]);

  return [value, setValue];
};

export default useLocalStorage;

/* EXAMPLE
const App = () => {
  const [name, setName] = useLocalStorage('name', 'John Doe');

  return (
    <div>
      <input value={name} onChange={(e) => setName(e.target.value)} />
      <p>Hello, {name}!</p>
    </div>
  );
};
*/
